import { useLayoutEffect, useSyncExternalStore } from 'react';

/**
 * The phone surface (docs/MOBILE.md, docs/THEMING.md).
 *
 * `useTouchSurface()` marks the document as a touch surface while it is
 * mounted: it sets `data-surface="touch"` on `<html>` (the attribute the
 * `[data-surface="touch"]` arms in index.css key on) and keeps the `.dark`
 * class in step with `prefers-color-scheme`, following later changes too. On
 * unmount both are put back to what they were before (the restore pattern of
 * `useApplyThemePreset`). Callers enable it only on a touch device
 * (`useIsCoarsePointer`), so desktop rendering is unchanged.
 *
 * Runs as a layout effect so the touch palette is in place in the first
 * painted frame (no flash of the desktop palette).
 *
 * Whether a touch surface is active is published through a tiny external
 * store, so components mounted outside the surface's tree (the app-wide
 * Toaster) can follow it: `useIsTouchSurfaceActive()`.
 */

const DARK_QUERY = '(prefers-color-scheme: dark)';
const COARSE_POINTER_QUERY = '(pointer: coarse)';

// --- the shared surface -------------------------------------------------------
//
// The document has one touch surface, however many components ask for it. The
// DOM writes are owned here, not by each hook instance: the first mount (0 -> 1)
// snapshots `data-surface` and `.dark`, sets the surface and starts the one
// `prefers-color-scheme` listener; the last unmount (1 -> 0) stops the listener
// and puts both back. Mounts in between only count, so nested or overlapping
// instances compose and an inner unmount never restores a stale snapshot.

let activeCount = 0;
const listeners = new Set<() => void>();
let teardown: (() => void) | null = null;

function emit() {
  for (const listener of listeners) listener();
}

function activate() {
  const root = document.documentElement;
  const previousSurface = root.getAttribute('data-surface');
  const previousDark = root.classList.contains('dark');

  root.setAttribute('data-surface', 'touch');

  const media = typeof window.matchMedia === 'function' ? window.matchMedia(DARK_QUERY) : null;
  const applyScheme = () => {
    root.classList.toggle('dark', media?.matches ?? false);
  };
  applyScheme();
  media?.addEventListener('change', applyScheme);

  teardown = () => {
    media?.removeEventListener('change', applyScheme);
    if (previousSurface === null) root.removeAttribute('data-surface');
    else root.setAttribute('data-surface', previousSurface);
    root.classList.toggle('dark', previousDark);
  };
}

function acquire() {
  activeCount += 1;
  if (activeCount === 1) activate();
  emit();
}

function release() {
  activeCount -= 1;
  if (activeCount === 0) {
    teardown?.();
    teardown = null;
  }
  emit();
}

function subscribeActive(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getActiveSnapshot() {
  return activeCount > 0;
}

function getActiveServerSnapshot() {
  return false;
}

/** True while at least one `useTouchSurface` is mounted and enabled. */
export function useIsTouchSurfaceActive(): boolean {
  return useSyncExternalStore(subscribeActive, getActiveSnapshot, getActiveServerSnapshot);
}

/**
 * Mark the document as a touch surface while mounted. Pass `enabled = false`
 * to leave the document alone (hooks cannot be called conditionally). Safe to
 * mount more than once: the document stays a touch surface until the last
 * instance unmounts, and only then is restored.
 */
export function useTouchSurface(enabled = true): void {
  useLayoutEffect(() => {
    if (!enabled) return;
    acquire();
    return release;
  }, [enabled]);
}

// --- coarse pointer ----------------------------------------------------------

function subscribeCoarsePointer(listener: () => void) {
  if (typeof window.matchMedia !== 'function') return () => {};
  const media = window.matchMedia(COARSE_POINTER_QUERY);
  media.addEventListener('change', listener);
  return () => media.removeEventListener('change', listener);
}

function getCoarsePointerSnapshot() {
  return typeof window.matchMedia === 'function' && window.matchMedia(COARSE_POINTER_QUERY).matches;
}

/**
 * True on a device whose primary pointer is a finger (`(pointer: coarse)`).
 * Read synchronously on the first render, so a page that gates
 * `useTouchSurface` on it never paints the desktop look first.
 */
export function useIsCoarsePointer(): boolean {
  return useSyncExternalStore(subscribeCoarsePointer, getCoarsePointerSnapshot, () => false);
}
